import type { paths } from '@low6-pickem/api-types';

export type ApiFnReturnType<FnType extends (...args: any) => Promise<any>> = Awaited<
  ReturnType<FnType>
>;

type HttpMethod = 'get' | 'post' | 'put' | 'patch' | 'delete';

export type RequestBody<Path extends keyof paths, Method extends HttpMethod> = paths[Path] extends {
  [K in Method]: { requestBody?: { content: { 'application/json': infer Body } } };
}
  ? Body
  : never;

export type ResponseBody<
  Path extends keyof paths,
  Method extends HttpMethod,
> = paths[Path] extends {
  [K in Method]: { responses: { 200: { content: { 'application/json': infer Body } } } };
}
  ? Body
  : paths[Path] extends {
        [K in Method]: { responses: { 201: { content: { 'application/json': infer Body } } } };
      }
    ? Body
    : unknown;

// Legacy type names for backward compatibility
export type ExtractRequestBody<
  Path extends keyof paths,
  Method extends keyof paths[Path],
> = RequestBody<Path, Method extends HttpMethod ? Method : never>;

export type ExtractResponseBody<
  Path extends keyof paths,
  Method extends keyof paths[Path],
> = ResponseBody<Path, Method extends HttpMethod ? Method : never>;

export const queryKeys = {
  pickems: {
    all: ['pickems'] as const,
    lists: () => [...queryKeys.pickems.all, 'list'] as const,
    list: (filters?: any) => [...queryKeys.pickems.lists(), filters] as const,
    details: () => [...queryKeys.pickems.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.pickems.details(), id] as const,
    entries: (id: string, params?: { page?: number; limit?: number; search?: string }) =>
      [...queryKeys.pickems.detail(id), 'entries', params] as const,
    userSelections: (pickemId: string, userId: string) =>
      [...queryKeys.pickems.detail(pickemId), 'user-selections', userId] as const,
    scoringStatus: ['pickems', 'scoring-status'] as const,
  },
  questions: {
    all: ['questions'] as const,
    lists: () => [...queryKeys.questions.all, 'list'] as const,
    list: (filters?: any) => [...queryKeys.questions.lists(), filters] as const,
    details: () => [...queryKeys.questions.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.questions.details(), id] as const,
  },
  leagues: {
    all: ['leagues'] as const,
    lists: () => [...queryKeys.leagues.all, 'list'] as const,
    list: (filters?: any) => [...queryKeys.leagues.lists(), filters] as const,
    details: () => [...queryKeys.leagues.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.leagues.details(), id] as const,
  },
  leaderboard: {
    all: ['leaderboard'] as const,
    pickem: (id: string) => [...queryKeys.leaderboard.all, 'pickem', id] as const,
  },
  settings: {
    all: ['settings'] as const,
    sports: () => [...queryKeys.settings.all, 'sports'] as const,
  },
  devData: {
    all: ['dev-data'] as const,
  },
  feed: {
    all: ['feed'] as const,
    sports: () => [...queryKeys.feed.all, 'sports'] as const,
    leagues: (sportId: string) => [...queryKeys.feed.all, 'leagues', sportId] as const,
    teams: (leagueId: string) => [...queryKeys.feed.all, 'teams', leagueId] as const,
    players: (teamId: string) => [...queryKeys.feed.all, 'players', teamId] as const,
    playerById: (leagueId: string, playerId: string) =>
      [...queryKeys.feed.all, 'player', leagueId, playerId] as const,
    matches: (leagueId: string, date?: string, from?: string, to?: string) =>
      [...queryKeys.feed.all, 'matches', leagueId, date, from, to] as const,
    fixtures: (leagueId: string, date?: string, from?: string, to?: string) =>
      [...queryKeys.feed.all, 'fixtures', leagueId, date, from, to] as const,
    fixtureById: (fixtureId: string) => [...queryKeys.feed.all, 'fixture', fixtureId] as const,
    statTypes: (sportId: string) => [...queryKeys.feed.all, 'stat-types', sportId] as const,
    teamRosterStats: (leagueId: string, teamId: string) =>
      [...queryKeys.feed.all, 'team-roster-stats', leagueId, teamId] as const,
  },
  tenants: {
    all: ['tenants'] as const,
    lists: () => [...queryKeys.tenants.all, 'list'] as const,
    list: (filters?: any) => [...queryKeys.tenants.lists(), filters] as const,
    details: () => [...queryKeys.tenants.all, 'detail'] as const,
    detail: (slug: string) => [...queryKeys.tenants.details(), slug] as const,
  },
  meta: {
    all: ['meta'] as const,
  },
  lobbies: {
    all: ['lobbies'] as const,
    tenantLobbies: (tenantId: string) => [...queryKeys.lobbies.all, 'tenant', tenantId] as const,
    detail: (lobbyId: string) => [...queryKeys.lobbies.all, 'detail', lobbyId] as const,
    templates: (tenantId: string) => [...queryKeys.lobbies.all, 'templates', tenantId] as const,
  },
  squares: {
    all: ['squares'] as const,
    lists: () => [...queryKeys.squares.all, 'list'] as const,
    list: (filters?: any) => [...queryKeys.squares.lists(), filters] as const,
    details: () => [...queryKeys.squares.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.squares.details(), id] as const,
    periodResults: (id: string | null) =>
      [...queryKeys.squares.detail(id || ''), 'period-results'] as const,
    entries: (id: string | null, params?: { page?: number; limit?: number; search?: string }) =>
      [...queryKeys.squares.detail(id || ''), 'entries', params] as const,
    reservations: (
      id: string | null,
      params?: { page?: number; limit?: number; search?: string }
    ) => [...queryKeys.squares.detail(id || ''), 'reservations', params] as const,
  },
  liveScoring: {
    all: ['live-scoring'] as const,
    pickems: () => [...queryKeys.liveScoring.all, 'pickems'] as const,
    status: () => [...queryKeys.liveScoring.all, 'status'] as const,
    questionHasOverrides: (questionId: string) =>
      [...queryKeys.liveScoring.all, 'question', questionId, 'has-overrides'] as const,
    feedStatuses: (fixtureIds: readonly string[]) =>
      [...queryKeys.liveScoring.all, 'feed-statuses', fixtureIds] as const,
  },
  prizePayouts: {
    all: ['prize-payouts'] as const,
    lists: () => [...queryKeys.prizePayouts.all, 'list'] as const,
    list: (filters?: any) => [...queryKeys.prizePayouts.lists(), filters] as const,
    details: () => [...queryKeys.prizePayouts.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.prizePayouts.details(), id] as const,
  },
  matchStats: {
    all: ['match-stats'] as const,
    details: () => [...queryKeys.matchStats.all, 'detail'] as const,
    detail: (fixtureId: string) => [...queryKeys.matchStats.details(), fixtureId] as const,
    overrides: (fixtureId: string) =>
      [...queryKeys.matchStats.detail(fixtureId), 'overrides'] as const,
  },
} as const;
